import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Flex, DatePicker } from "antd"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"

import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./RangeSelector.module.css"

RangeSelector.propTypes = {
  params: PropTypes.shape({
    type: PropTypes.string,
    overview_range: PropTypes.string,
    date_gteq: PropTypes.string,
    date_lteq: PropTypes.string
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired
}

function RangeSelector({ params, onChangeFilters }) {
  const { t } = useTranslation()
  const { overview_range, date_gteq, date_lteq } = params
  const activeRange = overview_range || "current_month"
  const isCustomRange = activeRange === "total_by_scope"

  const todayMaxDate = useMemo(() => dayjs().endOf("date"), [])
  const todayStart = useMemo(() => dayjs().startOf("date"), [])

  const setFilterTarget = useCallback(
    (name, value) => onChangeFilters({ target: { name, value } }),
    [onChangeFilters]
  )

  useEffect(() => {
    if (!overview_range) setFilterTarget("overview_range", "current_month")
  }, [overview_range, setFilterTarget])

  useEffect(() => {
    if (isCustomRange) return
    let start, end
    if (activeRange === "current_month") {
      start = dayjs().startOf("month")
      end = dayjs().endOf("month")
    } else if (activeRange === "last_month") {
      start = dayjs().subtract(1, "month").startOf("month")
      end = dayjs().subtract(1, "month").endOf("month")
    } else if (activeRange === "last_30_days") {
      start = dayjs().subtract(30, "days").startOf("day")
      end = dayjs().endOf("day")
    }
    if (start && end) {
      setFilterTarget("date_gteq", start.format(DATE_FORMAT))
      setFilterTarget("date_lteq", end.format(DATE_FORMAT))
    }
  }, [activeRange, isCustomRange, setFilterTarget])

  useEffect(() => {
    if (!isCustomRange) return
    const defaultStart = dayjs(date_lteq || dayjs()).startOf("month").format(DATE_FORMAT)
    const defaultEnd = dayjs().format(DATE_FORMAT)
    if (!date_gteq) setFilterTarget("date_gteq", defaultStart)
    if (!date_lteq) setFilterTarget("date_lteq", defaultEnd)
  }, [isCustomRange, date_gteq, date_lteq, setFilterTarget])

  const [rangePickerOpen, setRangePickerOpen] = useState(false)

  const handleDateRangeChange = (dates) => {
    setRangePickerOpen(false)
    if (dates?.length === 2) {
      const [start, end] = dates
      setFilterTarget("date_gteq", start.format(DATE_FORMAT))
      setFilterTarget("date_lteq", end.format(DATE_FORMAT))
    }
  }

  const dateRangeValue = date_gteq && date_lteq ? [dayjs(date_gteq), dayjs(date_lteq)] : null

  const formattedDateRange = dateRangeValue
    ? `${dateRangeValue[0].format("MMM D")} - ${dateRangeValue[1].format("MMM D YYYY")}`
    : t("accounting.range_selector.select_date_range")

  return (
    <Flex align="center" gap={8} justify="end" wrap className={styles.rangeSelector}>
      {isCustomRange && (
        <div className={styles.rangePickerWrapper}>
          <button type="button" className={styles.rangePreview} onClick={() => setRangePickerOpen((open) => !open)}>
            <span className={styles.rangePreviewText}>{formattedDateRange}</span>
          </button>
          <DatePicker.RangePicker
            open={rangePickerOpen}
            onOpenChange={setRangePickerOpen}
            value={dateRangeValue}
            defaultPickerValue={dateRangeValue || [dayjs(), dayjs().add(1, "month")]}
            onChange={handleDateRangeChange}
            format={DATE_FORMAT}
            disabledDate={(current) => current && current.valueOf() >= todayStart.valueOf()}
            maxDate={todayMaxDate}
            className={styles.hiddenRangePicker}
            dropdownClassName={styles.rangePopup}
            // popupStyle={{ transform: "translateY(1.8rem)" }}
          />
        </div>
      )}
    </Flex>
  )
}

export default memo(RangeSelector)
